/**
 * EcosystemAdminRoute: strict per-tenant ecosystem-admin route guard.
 *
 * Gates a route on the caller having an `admin` EcosystemMembership in the
 * CURRENT tenant. This is the dominant guard for the per-tenant `/admin/*`
 * area (org approvals, ownership claims, the admin dashboard, and the
 * content editor at `/admin/content/:pageKey`); those server endpoints are
 * already gated by `isAdminOfCurrentEcosystem` (or `isContentAdmin`, which
 * delegates to it), and this guard mirrors that boundary client-side.
 *
 * STRICT semantics: apex / no-tenant URLs are rejected (redirect to `/`).
 * A `platformAdmin` who is NOT an ecosystem admin of the active tenant is
 * also rejected (no cross-mode override) — #262 removed the apex content
 * editor that used to need one.
 *
 * Loading-race handling (the P5-3a MembershipContext lesson): a guard that
 * redirects before BOTH `authLoading` and `membershipLoading` have settled
 * is a bug: it would bounce a legitimate admin to login on a hard refresh
 * before MembershipContext has had a chance to populate `role`. So the
 * guard waits on both signals and renders a spinner until they resolve.
 *
 * Resolution:
 *   - auth or membership still loading       -> centered spinner.
 *   - apex / no tenant context              -> redirect to `/`.
 *   - not signed in (or unverified)          -> redirect to /login.
 *   - signed in but not an ecosystem admin   -> redirect to /dashboard.
 *   - ecosystem admin of the current tenant  -> render children.
 *
 * This client gate is a UX affordance only. The server endpoints enforce
 * the real boundary (`isAdminOfCurrentEcosystem`); a non-admin who bypasses
 * this guard still gets a 403 from the API.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import { useMembership } from '@/shared/contexts/MembershipContext';

export default function EcosystemAdminRoute({ children }) {
    const { user, loading: authLoading } = useAuth();
    const { isPlatform } = useEcosystem();
    const { role, loading: membershipLoading } = useMembership();
    const location = useLocation();

    // Wait for BOTH auth and membership before deciding: see the
    // loading-race comment in the file header.
    if (authLoading || membershipLoading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                </Spinner>
            </Container>
        );
    }

    // Apex / no-tenant: strict guard rejects (no platform-admin override).
    // The /admin/* area is per-tenant; there is nothing meaningful to admin
    // here at the platform level. Platform super-admins use /platform-admin
    // (PlatformAdminRoute) instead.
    if (isPlatform) {
        return <Navigate to="/" replace />;
    }

    // Not signed in (or unverified): send to login, preserving the target.
    if (!user || !user.emailVerified) {
        const redirectTo = location.pathname + location.search;
        return (
            <Navigate
                to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
                replace
            />
        );
    }

    // Signed in but not an ecosystem admin of the active tenant: send to
    // dashboard, which is the established "you are signed in but lack
    // permission for this surface" landing.
    if (role !== 'admin') {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

EcosystemAdminRoute.propTypes = {
    children: PropTypes.node.isRequired,
};
